import SystemDashboardWidget from '@/models/SystemDashboardWidget'
import moment from 'moment'
import numeral from 'numeral'
import { DailyGameDeliveryResponse, fetchDailyGameDelivery, niceCeil } from './deliveryChartsContract'

/**
 * Last-30-days daily Disney requests & impressions, each divided by the number
 * of MLB games that ran that day, as a stacked bar (Impressions bottom at full
 * value, the Requests−Impressions increment on top so the stack sums to total
 * requests — impressions are a subset of requests) with the delivery-rate line
 * (imps÷requests) on a secondary % axis. Days with no games render as 0 (not
 * null) so the axis never reads NaN.
 *
 * Its own request (delivery-dashboards/daily-game-delivery), served from the
 * daily pre-cache.
 */
const S = {
  impressions: 'Impressions Rendered per game',
  requests: 'DCM Ad requests per game',
  rate: 'Delivery rate / game',
}

export default class DailyGameDeliveryChart extends SystemDashboardWidget {
  public title: string = 'Requests & Impressions per MLB game — last 30 days'

  public key: string = 'delivery-daily-game'

  public view: string = 'chart'

  public subView: string = 'bar_stacked'

  public size = { lg: 12, md: 12 }

  public async dataSource(): Promise<DailyGameDeliveryResponse> {
    return fetchDailyGameDelivery(false)
  }

  protected dataMapper(res: DailyGameDeliveryResponse): Promise<any> {
    const days = res.days || []
    const categories = days.map(d => moment(d.date).format('MM/DD'))

    // No-game days: per-game is 0 (not null) so bars are 0 and the axis stays 0-based.
    const impg = days.map(d => d.impressions_per_game ?? 0)
    const reqpg = days.map(d => d.requests_per_game ?? 0) // actual total requests/game
    const series = [
      // Impressions (bottom, full) + the Requests−Impressions increment on top, so
      // the stack sums to total requests (impressions are a subset of requests).
      { name: S.impressions, type: 'column', data: impg },
      { name: S.requests, type: 'column', data: reqpg.map((v, i) => Math.max(0, v - impg[i])) },
      { name: S.rate, type: 'line', data: days.map(d => d.delivery_rate) },
    ]

    const barMax = niceCeil(Math.max(0, ...reqpg))
    const rateValues = days.map(d => d.delivery_rate).filter((v): v is number => v != null)
    const rateMax = rateValues.length ? Math.max(0.2, Math.ceil(Math.max(...rateValues) / 0.2) * 0.2) : 1

    const count = (v: number) => numeral(Number.isFinite(v) ? v : 0).format('0,0')

    this.chartSettings = {
      chart: {
        type: 'line', stacked: true, height: 400, toolbar: { show: false }, fontFamily: 'inherit',
      },
      // Impressions blue, Requests yellow, rate line purple + smooth (matching
      // the monthly chart's line style).
      colors: ['#2563EB', '#F0B429', '#7C3AED'],
      plotOptions: {
        bar: {
          columnWidth: '55%',
          borderRadius: 2,
          // Total (= DCM Ad requests, the stack sum) on top of each bar, in the
          // same k/2-decimal style as the segments (109.22k). offsetY lifts it
          // clear of the bar so it doesn't read as another segment.
          dataLabels: { total: { enabled: true, offsetY: -10, formatter: (v: number) => (v ? numeral(v).format('0.00a') : ''), style: { fontSize: '10px', fontWeight: 700 } } },
        },
      },
      stroke: { width: [0, 0, 3], curve: 'smooth' },
      fill: { opacity: [0.95, 0.95, 1] },
      markers: { size: [0, 0, 4] },
      // Each segment shows its own value: Impressions (full) + the
      // Requests−Impressions increment. The total requests are on top. k / 2 decimals (109.22k).
      dataLabels: {
        enabled: true,
        enabledOnSeries: [0, 1],
        formatter: (v: number) => (v ? numeral(v).format('0.00a') : ''),
        style: { fontSize: '9px' },
      },
      legend: { position: 'top', horizontalAlign: 'center' },
      xaxis: { categories },
      // One yaxis per series, index-aligned. Bars [0..1] share the left scale
      // (stacked); the rate line [2] is the right % axis. min:0 avoids a NaN axis.
      yaxis: [
        {
          min: 0, max: barMax, title: { text: 'Per game' }, labels: { formatter: (v: number) => count(v) },
        },
        { min: 0, max: barMax, show: false },
        {
          opposite: true,
          min: 0,
          max: rateMax,
          title: { text: 'Delivery rate' },
          labels: { formatter: (v: number) => (v == null ? '' : numeral(v).format('0%')) },
        },
      ],
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          // Show ACTUAL per-series values (Impressions, Requests) — not the plotted
          // increment — and the rate as %.
          formatter: (v: number, o: any) => {
            if (o.seriesIndex === 2) {
              return v == null ? '—' : numeral(v).format('0.0%')
            }
            const actual = o.seriesIndex === 0 ? impg[o.dataPointIndex] : reqpg[o.dataPointIndex]
            return numeral(actual ?? 0).format('0,0')
          },
        },
      },
      grid: { borderColor: '#eef0f3' },
      total: 0,
      labels: [],
    }

    return Promise.resolve(series)
  }
}
